import { useMemo } from 'react';
import {
	FeeTierDisplayUnit,
	FeeTierTableRow,
	TakerFeeGroup,
} from 'src/components/Modals/FeeTierModal/types';
import { FeeTier } from '@velocity-exchange/sdk';
import { VOLUME_TIERS } from 'src/components/Modals/FeeTierModal/consts';

interface Params {
	feeTiers: FeeTier[] | undefined;
	takerFeeGroups: TakerFeeGroup[];
	displayUnit: FeeTierDisplayUnit;
}

export default function useFeeTierModalTable({
	feeTiers,
	takerFeeGroups,
	displayUnit,
}: Params) {
	const rows = useMemo(() => {
		const tableRows: FeeTierTableRow[] = [];

		if (!feeTiers) {
			return tableRows;
		}

		VOLUME_TIERS.forEach(({ title, display }, index) => {
			const feeTier = feeTiers[index];

			// A slot the program never wrote has zeroed denominators, which would
			// divide to NaN. Guard on those rather than on the fee itself, so a
			// tier legitimately configured at zero still gets a row.
			if (!feeTier?.feeDenominator || !feeTier.makerRebateDenominator) {
				return;
			}

			const takerFee = feeTier.feeNumerator / feeTier.feeDenominator;
			const makerFee = -(
				feeTier.makerRebateNumerator / feeTier.makerRebateDenominator
			);

			tableRows.push({
				tierIndex: index,
				tier: title,
				volume: `${!index ? '<' : '≥'} ${display}`,
				takerFees: takerFeeGroups.map(({ addonTenthBps }) =>
					getFeeString(
						takerFee + tenthBpsToFraction(addonTenthBps),
						displayUnit,
						false
					)
				),
				makerFee: getFeeString(makerFee, displayUnit, true),
			});
		});

		return tableRows;
	}, [feeTiers, takerFeeGroups, displayUnit]);

	return { rows };
}

/** Tenth-bps against FEE_DENOMINATOR (100_000 = 100%), matching `calculate_taker_fee`. */
function tenthBpsToFraction(tenthBps: number) {
	return tenthBps / 100_000;
}

function getFeeString(
	fee: number,
	displayUnit: FeeTierDisplayUnit,
	isMaker: boolean
) {
	return displayUnit === 'percentage'
		? `${(fee * 100).toFixed(isMaker ? 5 : 4)}%`
		: `${(fee * 10000).toFixed(isMaker ? 3 : 2)} bps`;
}
